import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const LANGUAGE_KEY = "reflecta.settings.language";

const dictionaries = {
  en: {
    "nav.journal": "Journal",
    "nav.goals": "Goals",
    "nav.calendar": "Calendar",
    "nav.analytics": "Analytics",
    "nav.admin": "Admin",
    "nav.profile": "Profile",
    "nav.logout": "Log Out",
    "nav.free": "Free",
    "nav.premium": "Premium",
    "header.journal": "My Journal",
    "header.goals": "My Goals",
    "header.calendar": "Calendar",
    "header.analytics": "Analytics",
    "header.settings": "Settings",
    "header.admin": "Admin",
    "common.loading": "Loading...",
    "common.close": "Close",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.deleting": "Deleting...",
    "common.edit": "Edit",
    "common.save": "Save",
    "common.saving": "Saving...",
    "common.error": "Error",
    "common.upgrade": "Upgrade",
    "common.goPremium": "Go Premium",
    "common.premium": "Premium",
    "common.noDate": "No date",
    "demo.mode": "Demo mode",
    "demo.remaining": "remaining",
    "demo.createAccount": "Create account",
    "checkout.title": "Activate Reflecta Premium",
    "checkout.description": "Your Premium plan is reserved. Open the upgrade window to use the current dev billing flow.",
    "upsell.chatReason": "AI chat is included in Premium.",
    "upsell.analyticsPeriodReason": "90/365 day analytics are included in Premium.",
    "upsell.analyticsQuestionsReason": "Reflection questions in Analytics are included in Premium.",
    "upsell.goalAcceptReason": "AI goal suggestions can be accepted directly with Premium.",
    "upsell.goalManageReason": "AI goal suggestions can be managed with Premium.",
    "upsell.euRegionReason": "The EU AI region is included in Premium.",
    "upsell.reflectionLimitReason": "You used the 3 free reflection questions for this entry.",
    "paywall.title": "Reflecta Premium",
    "paywall.price": "11.99 EUR / month",
    "paywall.featureChat": "AI chat",
    "paywall.featureGoals": "Goal suggestions",
    "paywall.featureAnalytics": "90/365 day analytics",
    "paywall.featureQuestions": "Unlimited reflection questions",
    "paywall.mockUpgrade": "Mock Upgrade (Dev)",
    "paywall.updating": "Updating...",
    "paywall.error": "Upgrade failed, please try again.",
    "auth.premiumBanner": "You will get the Premium plan right after signup.",
    "auth.signupTitle": "Sign up - Reflecta",
    "auth.loginTitle": "Log in - Reflecta",
    "auth.signupDescription": "Create your Reflecta account and start your AI-powered journaling practice.",
    "auth.loginDescription": "Log in to Reflecta - your AI-powered journal.",
    "auth.backHome": "Back to home",
    "auth.subtitle": "Your AI-powered journal",
    "auth.login": "Log In",
    "auth.signup": "Sign Up",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.emailPlaceholder": "you@example.com",
    "auth.passwordLoginPlaceholder": "Enter your password",
    "auth.passwordSignupPlaceholder": "At least 8 characters",
    "auth.showPassword": "Show",
    "auth.hidePassword": "Hide",
    "auth.createAccount": "Create Account",
    "auth.pleaseWait": "Please wait...",
    "auth.invalidEmail": "Please enter a valid email address.",
    "auth.shortPassword": "Password must be at least {{count}} characters.",
    "auth.failed": "Authentication failed.",
    "auth.devFailed": "Dev login failed. Check that the backend is running with DEV_MODE=true.",
    "auth.termsNotice": "By continuing you agree to our Terms and Privacy Policy.",
    "journal.search": "Search entries...",
    "journal.newEntry": "New Entry",
    "journal.editEntry": "Edit Entry",
    "journal.mood": "Mood",
    "journal.activities": "Activities",
    "journal.sleep": "Sleep",
    "journal.stress": "Stress",
    "journal.social": "Social",
    "journal.loading": "Loading entries...",
    "journal.loadMore": "Load more",
    "journal.deleteTitle": "Delete entry",
    "journal.deleteMessage": "This journal entry will be permanently deleted. This action cannot be undone.",
    "journal.deleteConfirm": "Delete entry",
    "journal.empty": "No journal entries yet. Click \"New Entry\" to add one!",
    "journal.createError": "Failed to create entry: {{message}}",
    "journal.updateError": "Failed to update entry: {{message}}",
    "journal.deleteError": "Failed to delete entry: {{message}}",
    "entry.title": "Title",
    "entry.titlePlaceholder": "What's on your mind today?",
    "entry.date": "Date",
    "entry.content": "Journal Entry",
    "entry.contentPlaceholder": "Write your thoughts here...",
    "entry.aiOn": "AI ON",
    "entry.ai": "AI",
    "entry.freeQuestions": "{{used}} of {{limit}} free questions used",
    "entry.aiLoading": "Loading AI question...",
    "entry.aiFailed": "Could not load AI question. Please try again.",
    "entry.freeLimitReached": "Free limit reached for this entry.",
    "entry.startTyping": "Start typing to get AI questions.",
    "entry.stateTitle": "Track Your State",
    "entry.sentiment": "Sentiment",
    "entry.sleepQuality": "Sleep Quality",
    "entry.stressLevel": "Stress Level",
    "entry.socialEngagement": "Social Engagement",
    "entry.required": "Please fill out Title, Content, and Date.",
    "entry.update": "Update Entry",
    "entry.save": "Save Entry",
    "entry.formatted": "Formatted",
    "entry.original": "Original",
    "entry.sentiments": "Sentiments",
    "entry.goals": "Goals",
    "filters.all": "All",
    "mood.terrible": "Terrible",
    "mood.bad": "Bad",
    "mood.okay": "Okay",
    "mood.good": "Good",
    "mood.great": "Great",
    "sleep.veryPoor": "Very Poor",
    "sleep.poor": "Poor",
    "sleep.fair": "Fair",
    "sleep.good": "Good",
    "sleep.excellent": "Excellent",
    "stress.veryLow": "Very Low",
    "stress.low": "Low",
    "stress.moderate": "Moderate",
    "stress.high": "High",
    "stress.veryHigh": "Very High",
    "social.veryIsolated": "Very Isolated",
    "social.isolated": "Isolated",
    "social.neutral": "Neutral",
    "social.social": "Social",
    "social.verySocial": "Very Social",
    "goals.title": "Goals & Priorities",
    "goals.new": "New Goal",
    "goals.suggestions": "Goal Suggestions",
    "goals.loadingSuggestions": "Loading suggestions...",
    "goals.noRecommendations": "No recommendations available yet.",
    "goals.failedRecommendations": "Failed to load recommendations.",
    "goals.loading": "Loading goals...",
    "goals.accept": "Accept",
    "goals.decline": "Decline",
    "goals.edit": "Edit Goal",
    "goals.create": "Create New Goal",
    "goals.deleteTitle": "Delete goal",
    "goals.deleteMessage": "This goal will be permanently deleted. This action cannot be undone.",
    "goals.deleteConfirm": "Delete goal",
    "goals.empty": "No goals set yet. Click \"New Goal\" to add one!",
    "goal.details": "Goal Details",
    "goal.title": "Goal Title",
    "goal.titlePlaceholder": "What do you want to achieve?",
    "goal.type": "Goal Type",
    "goal.oneTime": "One-time",
    "goal.recurring": "Recurring",
    "goal.category": "Category",
    "goal.categoryPlaceholder": "e.g., Health, Career, Personal",
    "goal.priority": "Priority",
    "goal.priorityLow": "Low",
    "goal.priorityMedium": "Medium",
    "goal.priorityHigh": "High",
    "goal.lowPriority": "Low Priority",
    "goal.mediumPriority": "Medium Priority",
    "goal.highPriority": "High Priority",
    "goal.targetDate": "Target Date",
    "goal.description": "Description",
    "goal.descriptionOptional": "Description (Optional)",
    "goal.descriptionPlaceholder": "Add details about your goal...",
    "goal.enhance": "Enhance with AI",
    "goal.enhancing": "Enhancing...",
    "goal.enhanceFailed": "Failed to enhance description.",
    "goal.acceptPreview": "Use enhanced text",
    "goal.discardPreview": "Discard",
    "goal.required": "Please fill out Title and Category.",
    "goal.targetRequired": "Please fill out the Target Date for One-time goals.",
    "goal.targetPast": "Target Date must be today or later.",
    "goal.progress": "Progress",
    "goal.complete": "complete",
    "goal.linkedEntries": "Linked entries",
    "calendar.loading": "Loading calendar data...",
    "calendar.failed": "Failed to load calendar data",
    "calendar.dayDetail": "Day detail",
    "calendar.closeDayDetail": "Close day detail",
    "calendar.newEntryForDay": "New entry for this day",
    "calendar.entries": "Entries",
    "calendar.goals": "Goals",
    "calendar.showLess": "Show less",
    "calendar.showAllEntries": "Show all {{count}} entries",
    "calendar.showAllGoals": "Show all {{count}} goals",
    "calendar.noEntries": "No entries on this day.",
    "calendar.noGoals": "No goals due on this day.",
    "analytics.title": "Analytics",
    "analytics.last7": "Last 7 Days",
    "analytics.last30": "Last 30 Days",
    "analytics.last90": "Last 90 Days",
    "analytics.lastYear": "Last Year",
    "analytics.loading": "Loading analytics...",
    "analytics.loadError": "Could not load analytics data. Please try again later.",
    "analytics.statistics": "Statistics",
    "analytics.totalEntries": "Total Entries",
    "analytics.entriesLogged": "Journal entries logged",
    "analytics.currentStreak": "Current Streak",
    "analytics.days": "days",
    "analytics.consecutiveDays": "Consecutive days",
    "analytics.averageWords": "Average Words",
    "analytics.wordsPerEntry": "Words per entry",
    "analytics.trends": "Trends",
    "analytics.wellbeingTrends": "Wellbeing Trends",
    "analytics.topActivities": "Top Activities",
    "analytics.noActivities": "No activities detected in this period yet.",
    "analytics.correlations": "Correlations",
    "analytics.correlationStrength": "Correlation strength",
    "analytics.insights": "Insights",
    "analytics.patterns": "Mood, sleep, stress and social patterns",
    "analytics.summary": "Summary",
    "analytics.keyInsights": "Key Insights",
    "analytics.summaryFailed": "The analytics summary could not be generated at this time. This might be due to a temporary issue with our insights provider. Please check back later.",
    "analytics.reflectionQuestion": "Reflection Question",
    "analytics.generatePrompt": "Generate one question from your current analytics period.",
    "analytics.generateFailed": "Could not generate a reflection question right now.",
    "analytics.generating": "Generating...",
    "analytics.generateQuestion": "Generate question",
    "settings.title": "Account Settings",
    "settings.subscription": "Subscription",
    "settings.tier": "Tier",
    "settings.source": "Source",
    "settings.account": "Account",
    "settings.mock": "Mock",
    "settings.currentPeriodEnd": "Current period end",
    "settings.downgrade": "Downgrade to Free",
    "settings.aiRegion": "AI Region",
    "settings.language": "Language",
    "settings.password": "Change Password",
    "settings.passwordChanged": "Password changed successfully.",
    "settings.currentPassword": "Current password",
    "settings.newPassword": "New password (min. 8 characters)",
    "settings.confirmPassword": "Confirm new password",
    "settings.passwordShort": "New password must be at least 8 characters.",
    "settings.passwordMismatch": "Passwords do not match.",
    "settings.passwordFailed": "Password could not be changed.",
    "settings.changeFailed": "Change failed, please try again.",
    "settings.dataExport": "Data Export",
    "settings.requestExport": "Request JSON export",
    "settings.requestingExport": "Requesting export...",
    "settings.exportRequested": "Export requested: {{id}}",
    "settings.exportFailed": "Data export could not be started.",
    "settings.deleteAccount": "Delete Account",
    "settings.delete": "Delete account",
    "settings.typeDelete": "Type DELETE",
    "settings.confirmDeletion": "Confirm deletion",
    "settings.deletePhrase": "Please type DELETE to confirm deletion.",
    "settings.schedulingDeletion": "Scheduling deletion...",
    "settings.deletionScheduled": "Deletion scheduled for {{date}}",
    "settings.deletionFailed": "Account deletion could not be started.",
    "landing.nav.features": "Features",
    "landing.nav.pricing": "Pricing",
    "landing.nav.faq": "FAQ",
    "landing.nav.login": "Log in",
    "landing.nav.signup": "Sign up",
    "landing.hero.srHeading": "Feeling overwhelmed this week? Reflect. Grow. Repeat.",
    "landing.hero.headline": "Feeling overwhelmed?",
    "landing.hero.claim": "Reflect. Grow. Repeat.",
    "landing.hero.description": "An AI journal for your hard days and hidden patterns.",
    "landing.hero.disclaimer": "Free Basic Tier forever. No credit card. Two minutes to your first entry.",
    "landing.hero.cta": "Master your Chaos",
    "landing.hero.emailPlaceholder": "you@example.com",
    "landing.hero.emailOptOut": "No marketing. Just early-access to the private beta.",
    "landing.hero.phrase1": "Reflecta clears the noise.",
    "landing.hero.phrase2": "Reflecta brings clarity.",
    "landing.hero.phrase3": "Reflecta untangles your thoughts.",
    "landing.marquee.eyebrow": "Sound familiar?",
    "landing.marquee.headline": "You are not behind on your life. You are missing the mirror.",
    "landing.marquee.verified": "Verified Reflecta user",
    "landing.marquee.role.student": "Student, {{city}}",
    "landing.marquee.role.founder": "Founder",
    "landing.marquee.role.therapist": "Therapist-in-training",
    "landing.marquee.metric.streak": "Streak",
    "landing.marquee.metric.streakValue": "147 days",
    "landing.marquee.metric.entries": "Entries",
    "landing.marquee.metric.moodDelta": "Mood Δ",
    "landing.marquee.review1": "What convinced me to trust Reflecta was knowing my journal entries stay on secure EU servers. I get thoughtful AI insights without feeling like I'm sacrificing my privacy.",
    "landing.marquee.review2": "I am always setting goals and losing sight of them soon after. Reflecta doesn't just remind me what I'm working toward - the AI helps me understand why I'm stuck and suggests realistic steps to keep moving forward. For the first time, my goals feel achievable.",
    "landing.marquee.review3": "I've tried a lot of journaling apps, but Reflecta is the first one that has genuinely helped me understand my thoughts better. The AI picks up on patterns and connections I would have never noticed on my own.",
    "landing.marquee.review4": "My head goes in circles at night. A blank page is too much. I need a small prompt and five quiet minutes.",
    "landing.marquee.review5": "I want to track sleep, stress, and mood without yet another dashboard. Just journal, and have it noticed.",
    "landing.journal.card.live": "Live",
    "landing.analytics.card.title": "Your last 7 days",
    "landing.analytics.card.mood": "Mood",
    "landing.analytics.card.sleep": "Sleep",
    "landing.analytics.card.stress": "Stress",
    "landing.analytics.card.social": "Social",
    "landing.analytics.card.avgMood": "Avg mood",
    "landing.analytics.card.avgSleep": "Avg sleep",
    "landing.analytics.card.entries": "Entries",
    "landing.analytics.card.streak": "Streak",
    "landing.analytics.card.insight": "You sleep better on days you journaled.",
    "landing.analytics.card.days": "days",
    "landing.chatbot.card.name": "Reflecta AI",
    "landing.chatbot.card.subtitle": "Always listening, always learning.",
    "landing.chatbot.card.aiMsg1": "You have written \"I am tired\" three times this week, but you also said yes to two extra meetings. Want to look at where the energy is going?",
    "landing.chatbot.card.userMsg": "Yeah. I keep saying yes and then resenting it. What is one thing I could try this week?",
    "landing.chatbot.card.aiMsg2": "Looking at last Thursday - you wrote you felt better after declining one thing",
    "landing.chatbot.insight": "Your stress jumps the day after a bad night, but you keep saying yes to social plans those evenings. Maybe you are filling the tiredness instead of resting. Want to talk about that?",
    "landing.chatbot.insightLabel": "Reflecta AI Insight",
    "landing.analytics.tryDemo": "Try demo",
    "landing.analyticsCard.avgMood": "Avg Mood",
    "landing.analyticsCard.avgSleep": "Avg Sleep",
    "landing.analyticsCard.streak": "Streak",
    "landing.analyticsCard.streakBest": "Best yet",
    "landing.analyticsCard.entries": "Entries",
    "landing.analyticsCard.thisMonth": "this month",
    "landing.analyticsCard.streakUnit": "d",
    "landing.analyticsCard.wellbeing": "Wellbeing Trends",
    "landing.analyticsCard.legendMood": "Mood",
    "landing.analyticsCard.legendSleep": "Sleep",
    "landing.analyticsCard.legendStress": "Stress",
    "landing.analyticsCard.dayMon": "Mon",
    "landing.analyticsCard.dayTue": "Tue",
    "landing.analyticsCard.dayWed": "Wed",
    "landing.analyticsCard.dayThu": "Thu",
    "landing.analyticsCard.dayFri": "Fri",
    "landing.analyticsCard.daySat": "Sat",
    "landing.analyticsCard.daySun": "Sun",
    "landing.analyticsCard.hovering": "Hovering",
    "landing.analyticsCard.syncNote": "all charts sync.",
    "landing.analyticsCard.sentimentMix": "Sentiment Mix",
    "landing.analyticsCard.last14Days": "last 14 days",
    "landing.analyticsCard.sentProud": "Proud",
    "landing.analyticsCard.sentCalm": "Calm",
    "landing.analyticsCard.sentStressed": "Stressed",
    "landing.analyticsCard.sentTired": "Tired",
    "landing.analyticsCard.sentInspired": "Inspired",
    "landing.analyticsCard.sleepShort": "Sleep",
    "landing.analyticsCard.sleepAria": "Sleep quality 78 percent",
    "landing.analyticsCard.aiCorrelation": "AI correlation",
    "landing.analyticsCard.insightBody": "Mood and sleep moved together this week (r = 0.81). The two days under 4h sleep both produced your lowest sentiment scores.",
    "landing.features.eyebrow": "What Reflecta does",
    "landing.features.headline": "Not another mood tracker.",
    "landing.features.description": "Stop wondering why some weeks drain you. Reflecta connects your mood, sleep, and stress - and tells you what your own data already knows.",
    "landing.journal.title": "A journal that asks the right question.",
    "landing.journal.body": "No blank page. No pressure. Just one thoughtful question that helps you make sense of your day.",
    "landing.journal.bullet1": "A simple check-in: mood, sleep, stress, and social energy.",
    "landing.journal.bullet2": "One AI question: delivered after you're done writing, never interrupting.",
    "landing.journal.bullet3": "Works anywhere: on the train, in bed, between meetings.",
    "landing.analytics.title": "Patterns you would never spot alone.",
    "landing.analytics.body": "Every entry becomes a data point. Reflecta turns them into weekly, monthly, and yearly views of how your life actually feels.",
    "landing.analytics.bullet1": "Mood, sleep, stress and social trends over time.",
    "landing.analytics.bullet2": "Correlations that surface what really moves the needle.",
    "landing.analytics.bullet3": "Weekly summaries you actually want to read.",
    "landing.calendar.title": "Your year at a glance.",
    "landing.calendar.body": "See every entry, every goal, every mood at once. Zoom in on any day, or scan the whole month for the shape of your life.",
    "landing.calendar.bullet1": "Color-coded moods across every day.",
    "landing.calendar.bullet2": "Goals plotted next to the entries that moved them.",
    "landing.calendar.bullet3": "Tap any day for the full context.",
    "landing.chatbot.eyebrow": "Second opinion, on demand",
    "landing.chatbot.title": "A chatbot that knows your story.",
    "landing.chatbot.bullet1": "Reflecta remembers your story.",
    "landing.chatbot.bullet2": "Reflecta finds hidden patterns.",
    "landing.chatbot.bullet3": "Reflecta offers clarity, not advice.",
    "landing.pricing.eyebrow": "Pricing",
    "landing.pricing.headline": "Start free. Upgrade when it earns it.",
    "landing.pricing.description": "The Basic tier is free forever. Premium unlocks the parts that turn journaling into insight.",
    "landing.pricing.monthly": "Monthly",
    "landing.pricing.annual": "Annual",
    "landing.pricing.annualSave": "Save 17%",
    "landing.pricing.basic": "Basic",
    "landing.pricing.premium": "Premium",
    "landing.pricing.free": "Free forever",
    "landing.pricing.freeSuffix": "forever",
    "landing.pricing.perMonth": "/ month",
    "landing.pricing.perYearNote": "(119.99 EUR / year)",
    "landing.pricing.startFree": "Start free",
    "landing.pricing.getPremium": "Get Premium",
    "landing.pricing.feature1": "Daily journaling without limits",
    "landing.pricing.feature2": "Basic analytics (7 + 30 day)",
    "landing.pricing.feature3": "3 AI reflection questions per entry",
    "landing.pricing.feature4": "Unlimited AI reflection questions",
    "landing.pricing.feature5": "Reflecta Chatbot (AI chat)",
    "landing.pricing.feature6": "AI goal recommendations",
    "landing.pricing.feature7": "Advanced analytics (90 + 365 day, AI correlations)",
    "landing.pricing.feature8Free": "Standard AI models (US region only)",
    "landing.pricing.feature8Premium": "Better AI models with EU-region option (your choice)",
    "landing.cta.eyebrow": "Ready?",
    "landing.cta.headline": "Two minutes tonight. A pattern by next Sunday.",
    "landing.cta.description": "Reflecta is free to start. No credit card, no lock-in, no noise.",
    "landing.cta.button": "Master your Chaos",
    "landing.footer.tagline": "An AI journal for your hard days and hidden patterns.",
    "landing.footer.product": "Product",
    "landing.footer.company": "Company",
    "landing.footer.legal": "Legal",
    "landing.footer.impressum": "Impressum",
    "landing.footer.datenschutz": "Privacy",
    "landing.footer.agb": "Terms",
    "landing.footer.copyright": "(c) 2026 Reflecta GmbH",
    "landing.footer.language": "Language",
    "landing.footer.backTop": "Back to top",
    "landing.footer.cookieSettings": "Cookie settings",
    "landing.email.optOutBody": "Reflecta will occasionally send tips and updates. Check this if you'd rather not.",
    "landing.email.optOutLabel": "Opt out of marketing emails",
    "landing.seo.title": "Reflecta - An AI journal for your hard days and hidden patterns.",
    "landing.seo.description": "Feeling overwhelmed this week? Reflecta connects your mood, sleep, and stress and tells you what your own data already knows. Reflect. Grow. Repeat.",
    "legal.backHome": "Back to home",
    "legal.impressum.title": "Impressum",
    "legal.impressum.metaDescription": "Reflecta legal notice per section 5 TMG.",
    "legal.datenschutz.title": "Privacy Policy",
    "legal.datenschutz.metaDescription": "How Reflecta processes personal data under GDPR.",
    "legal.agb.title": "Terms and Conditions",
    "legal.agb.metaDescription": "Terms of use for Reflecta - the AI-powered journal.",
  },
  de: {
    "nav.journal": "Journal",
    "nav.goals": "Ziele",
    "nav.calendar": "Kalender",
    "nav.analytics": "Analysen",
    "nav.admin": "Admin",
    "nav.profile": "Profil",
    "nav.logout": "Abmelden",
    "nav.free": "Kostenlos",
    "nav.premium": "Premium",
    "header.journal": "Mein Journal",
    "header.goals": "Meine Ziele",
    "header.calendar": "Kalender",
    "header.analytics": "Analysen",
    "header.settings": "Einstellungen",
    "header.admin": "Admin",
    "common.loading": "Wird geladen...",
    "common.close": "Schließen",
    "common.cancel": "Abbrechen",
    "common.delete": "Löschen",
    "common.deleting": "Wird gelöscht...",
    "common.edit": "Bearbeiten",
    "common.save": "Speichern",
    "common.saving": "Wird gespeichert...",
    "common.error": "Fehler",
    "common.upgrade": "Upgrade",
    "common.goPremium": "Premium aktivieren",
    "common.premium": "Premium",
    "common.noDate": "Kein Datum",
    "demo.mode": "Demo-Modus",
    "demo.remaining": "verbleibend",
    "demo.createAccount": "Konto erstellen",
    "checkout.title": "Reflecta Premium aktivieren",
    "checkout.description": "Dein Premium-Plan ist vorgemerkt. Öffne das Upgrade-Fenster, um den aktuellen Entwicklungs-Abrechnungsfluss zu verwenden.",
    "upsell.chatReason": "KI-Chat ist in Premium enthalten.",
    "upsell.analyticsPeriodReason": "90/365-Tage-Analysen sind in Premium enthalten.",
    "upsell.analyticsQuestionsReason": "Reflexionsfragen in Analysen sind in Premium enthalten.",
    "upsell.goalAcceptReason": "KI-Zielvorschläge können mit Premium direkt übernommen werden.",
    "upsell.goalManageReason": "KI-Zielvorschläge können mit Premium verwaltet werden.",
    "upsell.euRegionReason": "Die EU-KI-Region ist in Premium enthalten.",
    "upsell.reflectionLimitReason": "Du hast die 3 freien Reflexionsfragen für diesen Eintrag genutzt.",
    "paywall.title": "Reflecta Premium",
    "paywall.price": "11,99 EUR / Monat",
    "paywall.featureChat": "KI-Chat",
    "paywall.featureGoals": "Zielvorschläge",
    "paywall.featureAnalytics": "90/365-Tage-Analysen",
    "paywall.featureQuestions": "Unbegrenzte Reflexionsfragen",
    "paywall.mockUpgrade": "Mock-Upgrade (Dev)",
    "paywall.updating": "Aktualisiere...",
    "paywall.error": "Upgrade fehlgeschlagen, bitte erneut versuchen.",
    "auth.premiumBanner": "Du bekommst den Premium-Plan direkt nach der Registrierung.",
    "auth.signupTitle": "Registrieren - Reflecta",
    "auth.loginTitle": "Anmelden - Reflecta",
    "auth.signupDescription": "Erstelle dein Reflecta-Konto und starte dein KI-gestütztes Journal.",
    "auth.loginDescription": "Melde dich bei Reflecta an - deinem KI-gestützten Journal.",
    "auth.backHome": "Zurück zur Startseite",
    "auth.subtitle": "Dein KI-gestütztes Journal",
    "auth.login": "Anmelden",
    "auth.signup": "Registrieren",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.emailPlaceholder": "du@example.com",
    "auth.passwordLoginPlaceholder": "Passwort eingeben",
    "auth.passwordSignupPlaceholder": "Mindestens 8 Zeichen",
    "auth.showPassword": "Anzeigen",
    "auth.hidePassword": "Verbergen",
    "auth.createAccount": "Konto erstellen",
    "auth.pleaseWait": "Bitte warten...",
    "auth.invalidEmail": "Bitte gib eine gültige E-Mail-Adresse ein.",
    "auth.shortPassword": "Das Passwort muss mindestens {{count}} Zeichen lang sein.",
    "auth.failed": "Authentifizierung fehlgeschlagen.",
    "auth.devFailed": "Dev-Login fehlgeschlagen. Prüfe, ob das Backend mit DEV_MODE=true läuft.",
    "auth.termsNotice": "Mit der Nutzung stimmst du unseren AGB und der Datenschutzerklärung zu.",
    "journal.search": "Einträge suchen...",
    "journal.newEntry": "Neuer Eintrag",
    "journal.editEntry": "Eintrag bearbeiten",
    "journal.mood": "Stimmung",
    "journal.activities": "Aktivitäten",
    "journal.sleep": "Schlaf",
    "journal.stress": "Stress",
    "journal.social": "Sozial",
    "journal.loading": "Einträge werden geladen...",
    "journal.loadMore": "Mehr laden",
    "journal.deleteTitle": "Eintrag löschen",
    "journal.deleteMessage": "Dieser Journal-Eintrag wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
    "journal.deleteConfirm": "Eintrag löschen",
    "journal.empty": "Noch keine Journal-Einträge. Klicke auf \"Neuer Eintrag\", um einen anzulegen.",
    "journal.createError": "Eintrag konnte nicht erstellt werden: {{message}}",
    "journal.updateError": "Eintrag konnte nicht aktualisiert werden: {{message}}",
    "journal.deleteError": "Eintrag konnte nicht gelöscht werden: {{message}}",
    "entry.title": "Titel",
    "entry.titlePlaceholder": "Was beschäftigt dich heute?",
    "entry.date": "Datum",
    "entry.content": "Journal-Eintrag",
    "entry.contentPlaceholder": "Schreibe deine Gedanken hier auf...",
    "entry.aiOn": "KI AN",
    "entry.ai": "KI",
    "entry.freeQuestions": "{{used}} von {{limit}} freien Fragen genutzt",
    "entry.aiLoading": "KI-Frage wird geladen...",
    "entry.aiFailed": "KI-Frage konnte nicht geladen werden. Bitte versuche es erneut.",
    "entry.freeLimitReached": "Freies Limit für diesen Eintrag erreicht.",
    "entry.startTyping": "Beginne zu schreiben, um KI-Fragen zu erhalten.",
    "entry.stateTitle": "Zustand erfassen",
    "entry.sentiment": "Stimmung",
    "entry.sleepQuality": "Schlafqualität",
    "entry.stressLevel": "Stresslevel",
    "entry.socialEngagement": "Soziale Aktivität",
    "entry.required": "Bitte Titel, Inhalt und Datum ausfüllen.",
    "entry.update": "Eintrag aktualisieren",
    "entry.save": "Eintrag speichern",
    "entry.formatted": "Formatiert",
    "entry.original": "Original",
    "entry.sentiments": "Gefühle",
    "entry.goals": "Ziele",
    "filters.all": "Alle",
    "mood.terrible": "Sehr schlecht",
    "mood.bad": "Schlecht",
    "mood.okay": "Okay",
    "mood.good": "Gut",
    "mood.great": "Sehr gut",
    "sleep.veryPoor": "Sehr schlecht",
    "sleep.poor": "Schlecht",
    "sleep.fair": "Mittel",
    "sleep.good": "Gut",
    "sleep.excellent": "Ausgezeichnet",
    "stress.veryLow": "Sehr niedrig",
    "stress.low": "Niedrig",
    "stress.moderate": "Mittel",
    "stress.high": "Hoch",
    "stress.veryHigh": "Sehr hoch",
    "social.veryIsolated": "Sehr isoliert",
    "social.isolated": "Isoliert",
    "social.neutral": "Neutral",
    "social.social": "Sozial",
    "social.verySocial": "Sehr sozial",
    "goals.title": "Ziele & Prioritäten",
    "goals.new": "Neues Ziel",
    "goals.suggestions": "Zielvorschläge",
    "goals.loadingSuggestions": "Vorschläge werden geladen...",
    "goals.noRecommendations": "Noch keine Empfehlungen verfügbar.",
    "goals.failedRecommendations": "Empfehlungen konnten nicht geladen werden.",
    "goals.loading": "Ziele werden geladen...",
    "goals.accept": "Übernehmen",
    "goals.decline": "Ablehnen",
    "goals.edit": "Ziel bearbeiten",
    "goals.create": "Neues Ziel erstellen",
    "goals.deleteTitle": "Ziel löschen",
    "goals.deleteMessage": "Dieses Ziel wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
    "goals.deleteConfirm": "Ziel löschen",
    "goals.empty": "Noch keine Ziele angelegt. Klicke auf \"Neues Ziel\", um eines anzulegen.",
    "goal.details": "Zieldetails",
    "goal.title": "Zieltitel",
    "goal.titlePlaceholder": "Was möchtest du erreichen?",
    "goal.type": "Zieltyp",
    "goal.oneTime": "Einmalig",
    "goal.recurring": "Wiederkehrend",
    "goal.category": "Kategorie",
    "goal.categoryPlaceholder": "z. B. Gesundheit, Karriere, Persönlich",
    "goal.priority": "Priorität",
    "goal.priorityLow": "Niedrig",
    "goal.priorityMedium": "Mittel",
    "goal.priorityHigh": "Hoch",
    "goal.lowPriority": "Niedrige Priorität",
    "goal.mediumPriority": "Mittlere Priorität",
    "goal.highPriority": "Hohe Priorität",
    "goal.targetDate": "Zieldatum",
    "goal.description": "Beschreibung",
    "goal.descriptionOptional": "Beschreibung (optional)",
    "goal.descriptionPlaceholder": "Füge Details zu deinem Ziel hinzu...",
    "goal.enhance": "Mit KI verbessern",
    "goal.enhancing": "Wird verbessert...",
    "goal.enhanceFailed": "Beschreibung konnte nicht verbessert werden.",
    "goal.acceptPreview": "Verbesserten Text verwenden",
    "goal.discardPreview": "Verwerfen",
    "goal.required": "Bitte Titel und Kategorie ausfüllen.",
    "goal.targetRequired": "Bitte ein Zieldatum für einmalige Ziele ausfüllen.",
    "goal.targetPast": "Das Zieldatum muss heute oder später liegen.",
    "goal.progress": "Fortschritt",
    "goal.complete": "abgeschlossen",
    "goal.linkedEntries": "Verknüpfte Einträge",
    "calendar.loading": "Kalenderdaten werden geladen...",
    "calendar.failed": "Kalenderdaten konnten nicht geladen werden",
    "calendar.dayDetail": "Tagesdetail",
    "calendar.closeDayDetail": "Tagesdetail schließen",
    "calendar.newEntryForDay": "Neuer Eintrag für diesen Tag",
    "calendar.entries": "Einträge",
    "calendar.goals": "Ziele",
    "calendar.showLess": "Weniger anzeigen",
    "calendar.showAllEntries": "Alle {{count}} Einträge anzeigen",
    "calendar.showAllGoals": "Alle {{count}} Ziele anzeigen",
    "calendar.noEntries": "Keine Einträge an diesem Tag.",
    "calendar.noGoals": "Keine Ziele an diesem Tag fällig.",
    "analytics.title": "Analysen",
    "analytics.last7": "Letzte 7 Tage",
    "analytics.last30": "Letzte 30 Tage",
    "analytics.last90": "Letzte 90 Tage",
    "analytics.lastYear": "Letztes Jahr",
    "analytics.loading": "Analysen werden geladen...",
    "analytics.loadError": "Analysedaten konnten nicht geladen werden. Bitte versuche es später erneut.",
    "analytics.statistics": "Statistiken",
    "analytics.totalEntries": "Einträge gesamt",
    "analytics.entriesLogged": "Journal-Einträge erfasst",
    "analytics.currentStreak": "Aktuelle Serie",
    "analytics.days": "Tage",
    "analytics.consecutiveDays": "Tage in Folge",
    "analytics.averageWords": "Durchschnittliche Wörter",
    "analytics.wordsPerEntry": "Wörter pro Eintrag",
    "analytics.trends": "Trends",
    "analytics.wellbeingTrends": "Wohlbefinden-Trends",
    "analytics.topActivities": "Top-Aktivitäten",
    "analytics.noActivities": "In diesem Zeitraum wurden noch keine Aktivitäten erkannt.",
    "analytics.correlations": "Korrelationen",
    "analytics.correlationStrength": "Korrelationsstärke",
    "analytics.insights": "Erkenntnisse",
    "analytics.patterns": "Muster aus Stimmung, Schlaf, Stress und Sozialem",
    "analytics.summary": "Zusammenfassung",
    "analytics.keyInsights": "Wichtige Erkenntnisse",
    "analytics.summaryFailed": "Die Analyse-Zusammenfassung konnte aktuell nicht erzeugt werden. Das kann an einem temporären Problem mit dem Erkenntnisdienst liegen. Bitte versuche es später erneut.",
    "analytics.reflectionQuestion": "Reflexionsfrage",
    "analytics.generatePrompt": "Erzeuge eine Frage aus deinem aktuellen Analysezeitraum.",
    "analytics.generateFailed": "Gerade konnte keine Reflexionsfrage erzeugt werden.",
    "analytics.generating": "Wird erzeugt...",
    "analytics.generateQuestion": "Frage erzeugen",
    "settings.title": "Kontoeinstellungen",
    "settings.subscription": "Abo",
    "settings.tier": "Tarif",
    "settings.source": "Quelle",
    "settings.account": "Konto",
    "settings.mock": "Mock",
    "settings.currentPeriodEnd": "Aktueller Zeitraum endet",
    "settings.downgrade": "Auf Kostenlos herabstufen",
    "settings.aiRegion": "KI-Region",
    "settings.language": "Sprache",
    "settings.password": "Passwort ändern",
    "settings.passwordChanged": "Passwort erfolgreich geändert.",
    "settings.currentPassword": "Aktuelles Passwort",
    "settings.newPassword": "Neues Passwort (min. 8 Zeichen)",
    "settings.confirmPassword": "Neues Passwort bestätigen",
    "settings.passwordShort": "Neues Passwort muss mindestens 8 Zeichen haben.",
    "settings.passwordMismatch": "Passwörter stimmen nicht überein.",
    "settings.passwordFailed": "Passwort konnte nicht geändert werden.",
    "settings.changeFailed": "Änderung fehlgeschlagen, bitte erneut versuchen.",
    "settings.dataExport": "Datenexport",
    "settings.requestExport": "JSON-Export anfordern",
    "settings.requestingExport": "Export wird angefordert...",
    "settings.exportRequested": "Export angefordert: {{id}}",
    "settings.exportFailed": "Datenexport konnte nicht gestartet werden.",
    "settings.deleteAccount": "Konto löschen",
    "settings.delete": "Konto löschen",
    "settings.typeDelete": "DELETE eingeben",
    "settings.confirmDeletion": "Löschung bestätigen",
    "settings.deletePhrase": "Bitte DELETE eingeben, um die Löschung zu bestätigen.",
    "settings.schedulingDeletion": "Löschung wird geplant...",
    "settings.deletionScheduled": "Löschung geplant für {{date}}",
    "settings.deletionFailed": "Kontolöschung konnte nicht gestartet werden.",
    "landing.nav.features": "Features",
    "landing.nav.pricing": "Preise",
    "landing.nav.faq": "FAQ",
    "landing.nav.login": "Anmelden",
    "landing.nav.signup": "Registrieren",
    "landing.hero.srHeading": "Wird dir alles zu viel? Reflect. Grow. Repeat.",
    "landing.hero.headline": "Wird dir alles zu viel?",
    "landing.hero.claim": "Reflect. Grow. Repeat.",
    "landing.hero.description": "Das KI-Journal für schwere Tage - und die Muster, die du sonst übersiehst.",
    "landing.hero.disclaimer": "Basic bleibt für immer kostenlos. Keine Kreditkarte nötig. In zwei Minuten schreibst du deinen ersten Eintrag.",
    "landing.hero.cta": "Ordne dein Chaos",
    "landing.hero.emailPlaceholder": "du@example.com",
    "landing.hero.emailOptOut": "Kein Marketing. Nur Zugang zur privaten Beta.",
    "landing.hero.phrase1": "Reflecta bringt Ruhe ins Chaos.",
    "landing.hero.phrase2": "Reflecta ordnet deine Gedanken.",
    "landing.hero.phrase3": "Reflecta macht den Kopf frei.",
    "landing.marquee.eyebrow": "Kommt dir bekannt vor?",
    "landing.marquee.headline": "Du hängst nicht hinterher. Dir fehlt nur ein ehrlicher Spiegel.",
    "landing.marquee.verified": "Verifizierter Reflecta-Nutzer",
    "landing.marquee.role.student": "Studentin, {{city}}",
    "landing.marquee.role.founder": "Gründer",
    "landing.marquee.role.therapist": "Therapeutin in Ausbildung",
    "landing.marquee.metric.streak": "Streak",
    "landing.marquee.metric.streakValue": "147 Tage",
    "landing.marquee.metric.entries": "Einträge",
    "landing.marquee.metric.moodDelta": "Stimmung Δ",
    "landing.marquee.review1": "Was mich überzeugt hat, Reflecta zu vertrauen: Meine Einträge bleiben auf sicheren EU-Servern. Ich bekomme durchdachte KI-Insights, ohne meine Privatsphäre aufzugeben.",
    "landing.marquee.review2": "Ich setze mir ständig Ziele und verliere sie kurz darauf aus den Augen. Reflecta erinnert mich nicht nur daran - die KI hilft mir zu verstehen, warum ich feststecke, und schlägt realistische Schritte vor. Zum ersten Mal fühlen sich meine Ziele erreichbar an.",
    "landing.marquee.review3": "Ich habe viele Journal-Apps ausprobiert, aber Reflecta ist die erste, die mir wirklich hilft, meine Gedanken zu verstehen. Die KI erkennt Muster und Zusammenhänge, die ich allein nie gesehen hätte.",
    "landing.marquee.review4": "Nachts drehen sich meine Gedanken im Kreis. Ein leeres Blatt ist zu viel. Ich brauche einen kleinen Anstoß und fünf ruhige Minuten.",
    "landing.marquee.review5": "Ich will Schlaf, Stress und Stimmung tracken - ohne noch ein Dashboard. Einfach schreiben, und dass es wahrgenommen wird.",
    "landing.journal.card.live": "Live",
    "landing.analytics.card.title": "Deine letzten 7 Tage",
    "landing.analytics.card.mood": "Stimmung",
    "landing.analytics.card.sleep": "Schlaf",
    "landing.analytics.card.stress": "Stress",
    "landing.analytics.card.social": "Sozial",
    "landing.analytics.card.avgMood": "Ø Stimmung",
    "landing.analytics.card.avgSleep": "Ø Schlaf",
    "landing.analytics.card.entries": "Einträge",
    "landing.analytics.card.streak": "Streak",
    "landing.analytics.card.insight": "An Tagen, an denen du geschrieben hast, schläfst du besser.",
    "landing.analytics.card.days": "Tage",
    "landing.chatbot.card.name": "Reflecta KI",
    "landing.chatbot.card.subtitle": "Immer da, immer am Lernen.",
    "landing.chatbot.card.aiMsg1": "Du hast diese Woche dreimal \"Ich bin müde\" geschrieben - und trotzdem zwei zusätzliche Meetings zugesagt. Wollen wir uns ansehen, wohin deine Energie geht?",
    "landing.chatbot.card.userMsg": "Ja. Ich sage immer zu und ärgere mich dann darüber. Was könnte ich diese Woche mal anders machen?",
    "landing.chatbot.card.aiMsg2": "Letzten Donnerstag - da hast du geschrieben, dass es dir besser ging, nachdem du einmal abgesagt hast",
    "landing.chatbot.insight": "Dein Stresslevel schnellt am Tag nach einer schlechten Nacht hoch - und trotzdem sagst du an genau diesen Abenden zu sozialen Plänen zu. Vielleicht überdeckst du die Müdigkeit, statt dich auszuruhen. Sollen wir darüber sprechen?",
    "landing.chatbot.insightLabel": "Reflecta KI-Insight",
    "landing.analytics.tryDemo": "Demo ausprobieren",
    "landing.analyticsCard.avgMood": "Ø Stimmung",
    "landing.analyticsCard.avgSleep": "Ø Schlaf",
    "landing.analyticsCard.streak": "Streak",
    "landing.analyticsCard.streakBest": "Bestwert",
    "landing.analyticsCard.entries": "Einträge",
    "landing.analyticsCard.thisMonth": "diesen Monat",
    "landing.analyticsCard.streakUnit": "T",
    "landing.analyticsCard.wellbeing": "Wohlbefinden-Trends",
    "landing.analyticsCard.legendMood": "Stimmung",
    "landing.analyticsCard.legendSleep": "Schlaf",
    "landing.analyticsCard.legendStress": "Stress",
    "landing.analyticsCard.dayMon": "Mo",
    "landing.analyticsCard.dayTue": "Di",
    "landing.analyticsCard.dayWed": "Mi",
    "landing.analyticsCard.dayThu": "Do",
    "landing.analyticsCard.dayFri": "Fr",
    "landing.analyticsCard.daySat": "Sa",
    "landing.analyticsCard.daySun": "So",
    "landing.analyticsCard.hovering": "Zeigt",
    "landing.analyticsCard.syncNote": "- alle Charts synchron.",
    "landing.analyticsCard.sentimentMix": "Gefühls-Mix",
    "landing.analyticsCard.last14Days": "letzte 14 Tage",
    "landing.analyticsCard.sentProud": "Stolz",
    "landing.analyticsCard.sentCalm": "Ruhig",
    "landing.analyticsCard.sentStressed": "Gestresst",
    "landing.analyticsCard.sentTired": "Müde",
    "landing.analyticsCard.sentInspired": "Inspiriert",
    "landing.analyticsCard.sleepShort": "Schlaf",
    "landing.analyticsCard.sleepAria": "Schlafqualität 78 Prozent",
    "landing.analyticsCard.aiCorrelation": "KI-Korrelation",
    "landing.analyticsCard.insightBody": "Stimmung und Schlaf sind diese Woche zusammen verlaufen (r = 0,81). Die zwei Tage mit unter 4 Stunden Schlaf hatten deine schlechtesten Stimmungswerte.",
    "landing.features.eyebrow": "So funktioniert Reflecta",
    "landing.features.headline": "Nicht noch ein Mood-Tracker.",
    "landing.features.description": "Hör auf zu rätseln, warum manche Wochen dich auslaugen. Reflecta verknüpft Stimmung, Schlaf und Stress - und zeigt dir, was deine eigenen Daten längst wissen.",
    "landing.journal.title": "Ein Journal, das die richtige Frage stellt.",
    "landing.journal.body": "Kein leeres Blatt. Kein Druck. Nur eine durchdachte Frage, die dir hilft, deinen Tag einzuordnen.",
    "landing.journal.bullet1": "Ein einfacher Check-in: Stimmung, Schlaf, Stress und soziale Energie.",
    "landing.journal.bullet2": "Eine KI-Frage: erst wenn du fertig bist - nie mittendrin.",
    "landing.journal.bullet3": "Funktioniert überall: im Zug, im Bett, zwischen Meetings.",
    "landing.analytics.title": "Muster, die du allein nie erkennen würdest.",
    "landing.analytics.body": "Jeder Eintrag wird zum Datenpunkt. Reflecta macht daraus Wochen-, Monats- und Jahresansichten - wie sich dein Leben wirklich anfühlt.",
    "landing.analytics.bullet1": "Trends für Stimmung, Schlaf, Stress und Soziales im Zeitverlauf.",
    "landing.analytics.bullet2": "Korrelationen, die zeigen, was wirklich den Unterschied macht.",
    "landing.analytics.bullet3": "Wochenzusammenfassungen, die du wirklich lesen willst.",
    "landing.calendar.title": "Dein Jahr auf einen Blick.",
    "landing.calendar.body": "Sieh jeden Eintrag, jedes Ziel, jede Stimmung auf einmal. Zoome in einen einzelnen Tag oder überblicke einen ganzen Monat.",
    "landing.calendar.bullet1": "Jeder Tag farbcodiert nach Stimmung.",
    "landing.calendar.bullet2": "Ziele neben den Einträgen, die sie vorangebracht haben.",
    "landing.calendar.bullet3": "Tippe auf einen Tag für alle Details.",
    "landing.chatbot.eyebrow": "Eine zweite Meinung. Auf Abruf.",
    "landing.chatbot.title": "Ein Chatbot, der weiß, was du durchgemacht hast.",
    "landing.chatbot.bullet1": "Reflecta erinnert sich, was du erlebt hast.",
    "landing.chatbot.bullet2": "Reflecta findet versteckte Muster.",
    "landing.chatbot.bullet3": "Reflecta schafft Klarheit - keine Ratschläge.",
    "landing.pricing.eyebrow": "Preise",
    "landing.pricing.headline": "Kostenlos starten. Upgraden, wenn es sich lohnt.",
    "landing.pricing.description": "Der Basic-Tarif ist für immer kostenlos. Premium schaltet die Funktionen frei, die aus deinen Einträgen echte Erkenntnisse machen.",
    "landing.pricing.monthly": "Monatlich",
    "landing.pricing.annual": "Jährlich",
    "landing.pricing.annualSave": "17% sparen",
    "landing.pricing.basic": "Basic",
    "landing.pricing.premium": "Premium",
    "landing.pricing.free": "Für immer kostenlos",
    "landing.pricing.freeSuffix": "für immer",
    "landing.pricing.perMonth": "/ Monat",
    "landing.pricing.perYearNote": "(119,99 EUR / Jahr)",
    "landing.pricing.startFree": "Kostenlos starten",
    "landing.pricing.getPremium": "Premium freischalten",
    "landing.pricing.feature1": "Journal ohne Limits",
    "landing.pricing.feature2": "Basis-Analysen (7 + 30 Tage)",
    "landing.pricing.feature3": "3 KI-Reflexionsfragen pro Eintrag",
    "landing.pricing.feature4": "Unbegrenzte KI-Reflexionsfragen",
    "landing.pricing.feature5": "Reflecta Chatbot",
    "landing.pricing.feature6": "KI-Zielvorschläge",
    "landing.pricing.feature7": "Erweiterte Analysen (90 + 365 Tage, KI-Korrelationen)",
    "landing.pricing.feature8Free": "Standard-KI-Modelle (nur US-Region)",
    "landing.pricing.feature8Premium": "Stärkere KI-Modelle - wähle EU- oder US-Region",
    "landing.cta.eyebrow": "Bereit?",
    "landing.cta.headline": "Zwei Minuten heute Abend. Ein Muster bis nächsten Sonntag.",
    "landing.cta.description": "Reflecta startet kostenlos. Keine Kreditkarte, keine Bindung, kein Werbelärm.",
    "landing.cta.button": "Ordne dein Chaos",
    "landing.footer.tagline": "Das KI-Journal für schwere Tage - und die Muster, die du sonst übersiehst.",
    "landing.footer.product": "Produkt",
    "landing.footer.company": "Unternehmen",
    "landing.footer.legal": "Rechtliches",
    "landing.footer.impressum": "Impressum",
    "landing.footer.datenschutz": "Datenschutz",
    "landing.footer.agb": "AGB",
    "landing.footer.copyright": "(c) 2026 Reflecta GmbH",
    "landing.footer.language": "Sprache",
    "landing.footer.backTop": "Nach oben",
    "landing.footer.cookieSettings": "Cookie-Einstellungen",
    "landing.email.optOutBody": "Reflecta schickt gelegentlich Tipps und Updates. Aktiviere das Kästchen, wenn du lieber keine bekommen willst.",
    "landing.email.optOutLabel": "Keine Marketing-Mails",
    "landing.seo.title": "Reflecta - Das KI-Journal für schwere Tage und die Muster, die du sonst übersiehst.",
    "landing.seo.description": "Wird dir alles zu viel? Reflecta verknüpft Stimmung, Schlaf und Stress und zeigt dir, was deine eigenen Daten längst wissen. Reflect. Grow. Repeat.",
    "legal.backHome": "Zurück zur Startseite",
    "legal.impressum.title": "Impressum",
    "legal.impressum.metaDescription": "Reflecta Impressum gemäß Paragraph 5 TMG.",
    "legal.datenschutz.title": "Datenschutzerklärung",
    "legal.datenschutz.metaDescription": "Wie Reflecta personenbezogene Daten gemäß DSGVO verarbeitet.",
    "legal.agb.title": "Allgemeine Geschäftsbedingungen",
    "legal.agb.metaDescription": "Nutzungsbedingungen für Reflecta - das KI-gestützte Journal.",
  },
};

const LanguageContext = createContext(null);

const readLanguage = () => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LANGUAGE_KEY);
  if (stored === "de" || stored === "en") return stored;
  return window.navigator?.language?.toLowerCase().startsWith("de") ? "de" : "en";
};

const format = (template, params = {}) => {
  return String(template).replace(/{{(\w+)}}/g, (_, key) => params[key] ?? "");
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readLanguage);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANGUAGE_KEY, language);
    }
  }, [language]);

  const value = useMemo(() => {
    const t = (key, params) => {
      const template = dictionaries[language]?.[key] ?? dictionaries.en[key] ?? key;
      return format(template, params);
    };
    return { language, setLanguage: setLanguageState, t };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return context;
};
